import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { setInLocalStorage } from '../../../utils';
import { resetOgiNearby, toggleAllChips, tooltipSlice, initialDrawerState } from '../../../reducers';
import {
    EmissionDrawerCardData,
    OGIInfo,
    PickInfoToPrioritize,
    TargetInfo,
    SnapPointIds,
    PageIds,
    StacDateBlock
} from '../../../types';

import { NoDataInfoDrawer } from '../../../components/NoDataInfoDrawer';
import { TargetDrawerWithCards } from '../../../components/EmissionsPageMap/LayerDataDrawer/DataSpecific';
import { IntroDrawer } from '../../../components/IntroDrawer';
import {
    EmissionDrawerWithCards,
    OGIDrawerWithCards
} from '../../../components/EmissionsPageMap/LayerDataDrawer/DrawerWithCards';

export interface EmissionsPageMapDrawersProps {
    drawerState: typeof initialDrawerState;
    drawerCardData: (EmissionDrawerCardData | OGIInfo | TargetInfo)[] | null;
    closeDrawer: () => void;
    setDrawerWidth: (width: number) => void;
    setHeightBottomDrawer: (snapPoint: SnapPointIds) => void;
    activeCardIndex: number;
    expandedAccordion: string | null;
    handleAccordionChange: (newExpandedAccordion: string, cardIndex: number) => void;
    handleMapInfo: (
        info: PickInfoToPrioritize[] | undefined,
        clickedNoData: boolean,
        showNoDataDrawer: boolean,
        addMultipleHighlights?: boolean
    ) => void;
    handleEmissionDrawerDateChange: (day: StacDateBlock, targetId: string) => void;
    page?: PageIds;
}

/**
 *
 */
export const EmissionsPageMapDrawers = ({
    drawerState,
    drawerCardData,
    closeDrawer,
    setDrawerWidth,
    setHeightBottomDrawer,
    activeCardIndex,
    expandedAccordion,
    handleAccordionChange,
    handleMapInfo,
    handleEmissionDrawerDateChange,
    page
}: EmissionsPageMapDrawersProps) => {
    const dispatch = useDispatch();
    const helpButtonRef = useRef<HTMLButtonElement>(null);

    const ogiNearbyEnabled = useSelector((state: RootState) => state.pages.emissions.layers.ogiLayers.ogiNearbyEnabled);

    return (
        <>
            <NoDataInfoDrawer open={drawerState.noData} onClose={closeDrawer} onWidth={setDrawerWidth} />
            <IntroDrawer
                data-testid="emissions-map-legend"
                open={drawerState.legend}
                onClose={(ev: Event) => {
                    ev.stopPropagation();
                    // prevent intro drawer from showing on load again
                    setInLocalStorage('introDismissed', true);
                    // hide IntroTooltip
                    dispatch(tooltipSlice.actions.hide());
                    // close mini-legend drawers when help drawer closes
                    dispatch(toggleAllChips(false));
                    // close drawer
                    closeDrawer();
                }}
                onWidth={setDrawerWidth}
                triggerRef={helpButtonRef}
            />
            {/* emissions details: distinct, dispersed, or both */}
            {drawerCardData && drawerState.emissionsDetails && (
                <EmissionDrawerWithCards
                    activeCardIndex={activeCardIndex}
                    cardData={drawerCardData as EmissionDrawerCardData[]}
                    expandedAccordion={expandedAccordion}
                    onAccordionChange={handleAccordionChange}
                    onClose={() => {
                        if (ogiNearbyEnabled) {
                            dispatch(resetOgiNearby());
                        }
                        closeDrawer();
                    }}
                    open={drawerState.emissionsDetails && !!drawerCardData}
                    setSnapPoint={setHeightBottomDrawer}
                    snapPoint={drawerState.bottomDrawerSnapPoint}
                    handleDateChange={handleEmissionDrawerDateChange}
                    page={page}
                />
            )}
            {drawerCardData && drawerState.ogiData && (
                <OGIDrawerWithCards
                    activeCardIndex={activeCardIndex}
                    cardData={drawerCardData as OGIInfo[]}
                    onClose={() => {
                        if (ogiNearbyEnabled) {
                            dispatch(resetOgiNearby());
                        }
                        closeDrawer();
                    }}
                    open={drawerState.ogiData && !!drawerCardData}
                    onAccordionChange={handleAccordionChange}
                    setSnapPoint={setHeightBottomDrawer}
                    snapPoint={drawerState.bottomDrawerSnapPoint}
                    closeDrawer={closeDrawer}
                />
            )}
            {drawerCardData && drawerState.targetData && (
                <TargetDrawerWithCards
                    activeCardIndex={0}
                    cardData={drawerCardData as TargetInfo[]}
                    onClose={closeDrawer}
                    open={drawerState.targetData && !!drawerCardData}
                    setSnapPoint={setHeightBottomDrawer}
                    snapPoint={drawerState.bottomDrawerSnapPoint}
                    handleInfoOnDateChange={(info: PickInfoToPrioritize[]) => handleMapInfo(info, false, false)}
                    page={page}
                />
            )}
        </>
    );
};
